// components/CrispChat.tsx
// 💬 Widget Crisp — support chat Glowp
// Désactivé sur mobile natif (Capacitor), actif sur web + PWA
// Masqué automatiquement sur les routes "focused workflow" où la barre
// d'action sticky en bas est critique (ex: /seller/import). Le widget
// Crisp recouvre le coin bas-droite et bouffe le clic du CTA principal
// — incident vendeur le 2026-05-12 (publier 3 brouillons impossible).

import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';

declare global {
  interface Window {
    $crisp: unknown[];
    CRISP_WEBSITE_ID: string;
  }
}

const CRISP_WEBSITE_ID = import.meta.env.VITE_CRISP_WEBSITE_ID as
  | string
  | undefined;

// Routes où le widget Crisp doit rester caché : workflow focalisé avec
// CTA sticky en bas. On lit window.location.hash directement à chaque
// changement plutôt que useLocation pour éviter une dépendance Router
// dans ce composant (rendu hors Routes dans App.tsx).
const HIDDEN_ROUTES = ['/seller/import'];

const isHiddenRoute = () => {
  const hash = window.location.hash || '';
  // hash ressemble à "#/seller/import" — on enlève le préfixe "#"
  const path = hash.startsWith('#') ? hash.slice(1) : hash;
  return HIDDEN_ROUTES.some(r => path.startsWith(r));
};

const setCrispHidden = (hidden: boolean) => {
  if (!window.$crisp) return;
  window.$crisp.push(['do', hidden ? 'chat:hide' : 'chat:show']);
};

export default function CrispChat() {
  useEffect(() => {
    // Pas de widget Crisp sur l'app mobile native
    if (Capacitor.isNativePlatform()) return;
    // Pas de Website ID configuré → widget désactivé
    if (!CRISP_WEBSITE_ID) return;
    // Déjà chargé
    if (window.$crisp) return;

    window.$crisp = [];
    window.CRISP_WEBSITE_ID = CRISP_WEBSITE_ID;

    const script = document.createElement('script');
    script.src = 'https://client.crisp.chat/l.js';
    script.async = true;
    document.head.appendChild(script);

    // Sync visibilité avec la route courante. On déclenche aussi au load
    // initial via le timer (Crisp prend ~500ms à initialiser).
    const sync = () => setCrispHidden(isHiddenRoute());
    const initialTimer = window.setTimeout(sync, 800);
    window.addEventListener('hashchange', sync);

    return () => {
      window.clearTimeout(initialTimer);
      window.removeEventListener('hashchange', sync);
      // Cleanup si le composant est démonté (peu probable mais propre)
      const el = document.querySelector(
        'script[src="https://client.crisp.chat/l.js"]'
      );
      el?.remove();
    };
  }, []);

  return null;
}
